import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  List,
  ListSubheader,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import { isEmpty } from 'lodash';

import { Network } from 'api';
import { useCapabilities, useNetworkCapabilities, useReleases, useSocialConnections } from 'hooks';
import { connectionPopup } from 'helpers';
import { Connection, SocialIcon } from 'components';
import theme from 'theme';

import LinkedInPages from './LinkedInPages';

const styles = {
  x: {
    backgroundColor: theme.custom.networks.x,
    color: theme.palette.common.white,

    '&:hover': {
      backgroundColor: darken(theme.custom.networks.x, 0.1),
      color: theme.palette.common.white,
    },
  },
  linkedin: {
    backgroundColor: theme.custom.networks.linkedin,
    color: theme.palette.common.white,

    '&:hover': {
      backgroundColor: darken(theme.custom.networks.linkedin, 0.1),
      color: theme.palette.common.white,
    },
  },
  list: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    height: 1,
    margin: theme.spacing(1, 0, 2),
  },
  learnMore: {
    color: theme.palette.common.black,
  },
  tooltip: {
    cursor: 'pointer',
  },
} as const;

const Connections: FC = () => {
  const { t } = useTranslation();
  const { hasLinkedin, hasTwitter } = useNetworkCapabilities();
  const { needs } = useCapabilities();
  const { connections, twitterAccounts, linkedinAccounts } = useSocialConnections();
  const releases = useReleases();

  const hasSmartScheduler = releases.includes('SmartScheduler');

  const showTwitterConnect =
    !twitterAccounts.length || needs('Sharing.Function.ConnectMultipleAccounts');
  const showLinkedInConnect =
    !linkedinAccounts.length || needs('Sharing.Function.ConnectMultipleAccounts');

  function renderHeader() {
    return (
      <ListSubheader disableGutters>
        <Grid container justifyContent="space-between" alignItems="flex-start">
          <Grid>
            <Typography variant="caption" color="textSecondary">
              {t('user:accounts')}
            </Typography>
          </Grid>

          <Grid>
            <Box marginRight={15}>
              <Typography variant="caption" color="textSecondary">
                {t('user:smartScheduler')}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={styles.divider} />
      </ListSubheader>
    );
  }

  function renderConnections() {
    return connections.map(account => {
      if (account.provider === Network.Twitter && hasTwitter) {
        return <Connection key={account.id} account={account} connectedAccounts />;
      }

      if (account.provider === Network.Linkedin && hasLinkedin) {
        return (
          <Connection key={account.id} account={account} connectedAccounts>
            <LinkedInPages accountId={account.id} />
          </Connection>
        );
      }

      return <Connection key={account.id} account={account} />;
    });
  }

  return (
    <Grid container>
      <Grid xs={3}>
        <Typography variant="h6">{t('user:connectedAccounts')}</Typography>
      </Grid>
      <Grid xs={9}>
        <List sx={!isEmpty(connections) && styles.list}>
          {hasSmartScheduler && !isEmpty(connections) && renderHeader()}
          {renderConnections()}
        </List>
        <Grid container spacing={2}>
          {hasTwitter && showTwitterConnect && (
            <Grid>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => connectionPopup(Network.Twitter)}
                startIcon={<SocialIcon state="inverted" provider="twitter" size={20} />}
                sx={styles.x}
              >
                {t('user:connectToX')}
              </Button>
            </Grid>
          )}
          {hasLinkedin && showLinkedInConnect && (
            <Grid>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => connectionPopup(Network.Linkedin)}
                startIcon={<SocialIcon state="inverted" provider="linkedin" size={20} />}
                sx={styles.linkedin}
              >
                {t('user:connectToLinkedIn')}
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Connections;
